export const START_CHAT_MESSAGE = 'Help Desk joined the chat'
export const PENDING_JOIN_PREFIX = '__PENDING_JOIN__:'
export const JOIN_GREETING =
  'Hello! Thank you for contacting our Skyniche support team. How can I assist you today?'
export const END_CHAT_MESSAGE = 'Chat ended'

export const isSystemMessage = (message) =>
  message?.contentType === 'system' || message?.message_type === 'system'

export const isEndMessage = (message) =>
  message?.contentType === 'end' ||
  message?.message_type === 'end' ||
  String(message?.text || message?.message || '')
    .replace(/<(.|\n)*?>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .trim() === END_CHAT_MESSAGE

export const isHelpDeskJoinMessage = (message) => {
  if (!isSystemMessage(message)) return false
  const text = String(message?.text || message?.message || '')
    .replace(/<(.|\n)*?>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .trim()
  if (!text || text === START_CHAT_MESSAGE) return true
  if (text.startsWith(PENDING_JOIN_PREFIX)) return false
  return /^you joined the chat$/i.test(text) || /^help desk joined the chat$/i.test(text)
}

export const parsePendingJoinMessage = (text = '') => {
  const normalized = String(text)
    .replace(/<(.|\n)*?>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .trim()
  if (!normalized.startsWith(PENDING_JOIN_PREFIX)) return null
  const payload = normalized.slice(PENDING_JOIN_PREFIX.length)
  const separator = payload.indexOf(':')
  if (separator < 0) return null
  const userId = Number(payload.slice(0, separator))
  const name = payload.slice(separator + 1).trim()
  if (!userId || !name) return null
  return { userId, name }
}

export const isPendingJoinMessage = (message) =>
  Boolean(parsePendingJoinMessage(message?.text || message?.message || ''))

export const getHelpDeskChatStarted = (messages = [], options = {}) => {
  const { fromCustomerView = false } = options
  let started = false
  messages.forEach((item) => {
    if (item?.isTyping) return
    if (isEndMessage(item)) started = false
    else if (isHelpDeskJoinMessage(item)) started = true
    else if (
      fromCustomerView &&
      item.type === 'received' &&
      item.contentType !== 'bot' &&
      !isSystemMessage(item) &&
      !isEndMessage(item)
    ) {
      started = true
    }
  })
  return started
}

export const getHelpDeskChatStartedInSession = (messages = [], options = {}) =>
  getHelpDeskChatStarted(getCurrentSessionMessages(messages), options)

export const hasEndedChat = (messages = []) =>
  messages.some((item) => !item?.isTyping && isEndMessage(item))

export const hasPendingUserMessage = (messages = [], userMessageType) => {
  let lastEndIndex = -1
  messages.forEach((item, index) => {
    if (!item?.isTyping && isEndMessage(item)) lastEndIndex = index
  })
  return messages.some(
    (item, index) =>
      index > lastEndIndex &&
      item.type === userMessageType &&
      !item?.isTyping &&
      !isSystemMessage(item) &&
      !isEndMessage(item),
  )
}

export const getCurrentSessionMessages = (messages = []) => {
  let lastEndIndex = -1
  messages.forEach((item, index) => {
    if (!item?.isTyping && isEndMessage(item)) lastEndIndex = index
  })
  return messages.filter((item, index) => index > lastEndIndex && !isEndMessage(item))
}

export const getLatestSessionMessages = (messages = []) => {
  let lastEndIndex = -1
  let previousEndIndex = -1
  messages.forEach((item, index) => {
    if (!item?.isTyping && isEndMessage(item)) {
      previousEndIndex = lastEndIndex
      lastEndIndex = index
    }
  })
  const lastVisible = [...messages].reverse().find((item) => !item?.isTyping)
  if (lastEndIndex >= 0 && lastVisible && isEndMessage(lastVisible)) {
    return messages.slice(previousEndIndex + 1, lastEndIndex + 1)
  }
  return messages.filter((item, index) => index > lastEndIndex)
}

export const isEndedGroupContact = (contact) =>
  contact?.conversation_type === 'group' &&
  (contact?.last_message_type === 'end' ||
    String(contact?.last_message || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .trim() === END_CHAT_MESSAGE)

export const getMessagesForContact = (messages = [], contact, compact = false) => {
  if (!compact) return messages
  if (isEndedGroupContact(contact)) {
    let lastEndIndex = -1
    let previousEndIndex = -1
    messages.forEach((item, index) => {
      if (!item?.isTyping && isEndMessage(item)) {
        previousEndIndex = lastEndIndex
        lastEndIndex = index
      }
    })
    if (lastEndIndex >= 0) {
      return messages.slice(previousEndIndex + 1, lastEndIndex + 1)
    }
  }
  return getLatestSessionMessages(messages)
}

export const hasActiveHelpDeskSession = (messages = []) =>
  getCurrentSessionMessages(messages).some((item) => !item?.isTyping)

/** Last known help-desk online flag for late-mounting chat UI (survives missed events). */
let lastHelpDeskOnlineStatus = null

export const getLastHelpDeskOnlineStatus = () => lastHelpDeskOnlineStatus

export const broadcastHelpDeskOnlineStatus = (online) => {
  lastHelpDeskOnlineStatus = Boolean(online)
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent('signwise-helpdesk-online', {
      detail: { online: lastHelpDeskOnlineStatus },
    }),
  )
}

const deliveredStorageKey = (userId) => `signwise-helpdesk-delivered:${userId}`

export const loadDeliveredMessageIds = (userId) => {
  if (!userId) return new Set()
  try {
    const raw = localStorage.getItem(deliveredStorageKey(userId))
    const list = JSON.parse(raw || '[]')
    return new Set((Array.isArray(list) ? list : []).map(String))
  } catch {
    return new Set()
  }
}

export const persistDeliveredMessageIds = (userId, messageIds = []) => {
  if (!userId) return
  const next = loadDeliveredMessageIds(userId)
  let changed = false
  messageIds.forEach((id) => {
    const key = String(id)
    if (!key || key.startsWith('local-') || next.has(key)) return
    next.add(key)
    changed = true
  })
  if (!changed && next.size === 0) return
  if (!changed) return
  try {
    localStorage.setItem(deliveredStorageKey(userId), JSON.stringify([...next]))
  } catch {
    // ignore quota / private mode
  }
}

/** Mark sent messages delivered and remember server IDs across refresh. */
export const markAndPersistSentDelivered = (userId, messages = [], { forceAllSent = false } = {}) => {
  const stored = loadDeliveredMessageIds(userId)
  const next = messages.map((item) => {
    if (item.type !== 'sent') return item
    if (forceAllSent || item.delivered === true || stored.has(String(item.id))) {
      return { ...item, delivered: true }
    }
    return item
  })
  persistDeliveredMessageIds(
    userId,
    next.filter((item) => item.type === 'sent' && item.delivered === true).map((item) => item.id),
  )
  return next
}

const pendingFeedbackStorageKey = (userId) => `signwise-helpdesk-pending-feedback:${userId}`

export const loadPendingFeedback = (userId) => {
  if (!userId) return null
  try {
    const raw = localStorage.getItem(pendingFeedbackStorageKey(userId))
    if (!raw) return null
    const data = JSON.parse(raw)
    if (!data?.conversationId) return null
    return data
  } catch {
    return null
  }
}

export const persistPendingFeedback = (userId, meta) => {
  if (!userId || !meta?.conversationId) return
  try {
    localStorage.setItem(pendingFeedbackStorageKey(userId), JSON.stringify(meta))
  } catch {
    // ignore quota / private mode
  }
}

export const clearPendingFeedback = (userId) => {
  if (!userId) return
  try {
    localStorage.removeItem(pendingFeedbackStorageKey(userId))
  } catch {
    // ignore
  }
}

export const isFeedbackCompleted = (chatRating) => {
  const rating = chatRating?.rating
  return rating === 'up' || rating === 'down' || rating === 'skip'
}
